import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/PasswordInput';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, ArrowLeft, ShieldCheck } from 'lucide-react';
import { authAPI } from '../services/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get('token') || '';

  const [token, setToken] = useState(tokenFromUrl);
  const [accountEmail, setAccountEmail] = useState('');
  const [accountError, setAccountError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const value = (tokenFromUrl || token || '').trim();
    if (!value) {
      setAccountEmail('');
      setAccountError('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authAPI.getResetPasswordAccount(value);
        if (!cancelled) {
          setAccountEmail(data?.email || '');
          setAccountError('');
        }
      } catch (error) {
        if (!cancelled) {
          setAccountEmail('');
          setAccountError(
            error.response?.data?.detail || 'This reset link is invalid or has expired.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tokenFromUrl, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token.trim()) {
      toast.error('Reset token is missing');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword({ token: token.trim(), new_password: newPassword });
      setSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          'Failed to reset password. Token may be invalid or expired.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#003380]/10 mb-3">
            <ShieldCheck className="h-7 w-7 text-[#003380]" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Reset Password</h1>
          <p className="text-sm text-slate-500 mt-1">Enter your new password below</p>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">
              {success ? 'Password Updated' : 'Set New Password'}
            </CardTitle>
            <CardDescription>
              {success
                ? 'Your password has been changed successfully.'
                : accountEmail
                  ? `Resetting password for ${accountEmail}`
                  : accountError || 'Choose a strong password for your account.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!success ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {!tokenFromUrl && (
                  <div className="space-y-2">
                    <Label htmlFor="token">Reset Token</Label>
                    <Input
                      id="token"
                      type="text"
                      placeholder="Paste your reset token"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      data-testid="reset-token-input"
                      required
                    />
                  </div>
                )}

                {accountEmail && (
                  <div
                    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    data-testid="reset-account-email"
                  >
                    Account: <span className="font-medium">{accountEmail}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 z-10 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <PasswordInput
                      id="new-password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10"
                      data-testid="new-password-input"
                      required
                      minLength={8}
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    At least 8 characters, with upper, lower, number, and special character
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 z-10 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <PasswordInput
                      id="confirm-password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      data-testid="confirm-password-input"
                      required
                      minLength={8}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#003380] hover:bg-[#002060]"
                  disabled={loading || Boolean(accountError)}
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    {accountEmail
                      ? `Password updated for ${accountEmail}. You can now sign in.`
                      : 'Your password has been updated. You can now sign in with your new password.'}
                  </p>
                </div>
                <Button
                  className="w-full bg-[#003380] hover:bg-[#002060]"
                  onClick={() => navigate('/login')}
                  data-testid="go-to-login-btn"
                >
                  Go to Login
                </Button>
              </div>
            )}

            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-[#003380] hover:underline"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
